import React, { useContext, useEffect, useState } from 'react';
import './SurfaceTractPage.scss';
import { useForm } from 'react-hook-form';
import Button from 'src/components/controls/forms/redesign/FormButton';
import SurfaceTract from 'src/models/SurfaceTract';
import useFetch from 'use-http';
import SurfaceTractCardRHF from './tract/components/SurfaceTractCard.redesign';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import { Url } from 'src/api';
import Project from 'src/models/Project';
import moment from 'moment';
import OwnershipContactCategoryForm from 'src/models/OwnershipContactCategoryForm';
import { Tag } from 'react-tag-autocomplete';
import GlobalOwnershipList from './TractComponents/OwnershipList.redesign';
import Ownership from 'src/models/tract_detail/Ownership';
import MineralTractList from './tract/components/MineralTractList';
import TractDetail from 'src/models/tract_detail/TractDetail';
import Horizon from 'src/models/tract_detail/Horizon';
import { SelectItem } from 'src/models/common/SelectItem';
import { AnyObject } from 'final-form';
import Abstract from 'src/models/Abstract';

const SurfaceTractPage = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const surfaceOwnershipsRef = React.createRef();

    const { getValues, setValue, handleSubmit, formState, register, errors, reset } = useForm();
    const { get, put, response } = useFetch();
    const [surfaceTract, setSurfaceTract] = useState<SurfaceTract>(undefined);
    const [projectData, setProjectData] = useState<Project>(undefined);
    const [ownershipContactCategories, setOwnershipContactCategories] = useState<OwnershipContactCategoryForm>(undefined);
    const [surfaceTags, setSurfaceTags] = useState<Tag[]>([]);
    const [surfaceAbstracts, setSurfaceAbstracts] = useState<Abstract[]>([]);
    const [fieldsUpdated, setFieldsUpdated] = useState<boolean>(false);
    const [grossAcres, setGrossAcres] = useState<number>(0);
    const [leaseBuyerList, setLeaseBuyerList] = useState<Array<SelectItem>>([]);

    useEffect(() => {
        loadSurfaceTract();
        getProjectData();
        getOwnershipContactCategories();
        getProjectStaff();
    }, []);

    useEffect(() => {
        if (!!surfaceTract) {
            reset(surfaceTract);
            setGrossAcres(surfaceTract?.acres);
        }
    }, [surfaceTract]);

    const updateGrossAcres = () => {
        let ref = getValues();
        if (ref.acres) {
            setGrossAcres(ref.grossAcres);
        }
    };

    const loadSurfaceTract = async () => {
        const result: SurfaceTract = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/surface-tracts/${appContext.surfaceTractNumber}`
        );

        if (response.ok) {
            if (result.recordTypeId) {
                result.recordDate = moment(result.recordDate).format('YYYY-MM-DD') as unknown as Date;
            }
            if (result.examinedHistories?.length > 0) {
                result.examinedHistories.forEach((eh) => {
                    eh.takeOffDate = moment(eh.takeOffDate).format('YYYY-MM-DD') as unknown as Date;
                    eh.examinedDate = moment(eh.examinedDate).format('YYYY-MM-DD') as unknown as Date;
                });
            }
            setSurfaceTract(result);
            if (result.tags?.length > 0) {
                setSurfaceTags(result.tags);
            }
            if (result.abstracts?.length > 0) {
                setSurfaceAbstracts(result.abstracts);
            }
        }
    };

    const getProjectData = async () => {
        const data = await get(Url.project.getProject(appContext, appContext.projectNumber));

        if (response.ok) {
            setProjectData(data);
        }
    };

    const getOwnershipContactCategories = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownershipcontactcategories`);

        if (response.ok) {
            setOwnershipContactCategories(result);
        }
    };

    const getProjectStaff = async () => {
        const result = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/staffing`);

        if (response.ok) {
            if (!!result) {
                setLeaseBuyerList(result && result.items.length > 0
                    ? result.items
                          .map((landman: AnyObject) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
                    : []
                );
            }    
        }
    };

    const updateSurfaceTags = (tags: Tag[]) => {
        setSurfaceTags([...tags]);
        setFieldsUpdated(true);
    };

    const updateSurfaceAbstracts = (abstracts: Abstract[]) => {
        setSurfaceAbstracts([...abstracts]);
        setFieldsUpdated(true);
    };

    const onSubmit = async (data: SurfaceTract) => {
        if (surfaceOwnershipsRef.current) {
            (surfaceOwnershipsRef.current as { saveForm: () => void }).saveForm();
        }

        data = formatSurfaceTract(data);
        data.ownerships = formatOwnerships(data.ownerships);
        data.mineralTracts = formatMineralTracts(data.mineralTracts);
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/tracts/surface-tracts`,
            data
        );

        if (response.ok) {
            setFieldsUpdated(false);
            loadSurfaceTract();
        }
    };

    const cancelFormChanges = () => {
        window.location.reload();
    };

    const formatSurfaceTract = (st: SurfaceTract) => {
        st.id = surfaceTract ? surfaceTract.id : 0;
        st.number = surfaceTract ? surfaceTract.number : 0;
        st.recordTypeId = st.recordTypeId ? parseInt(st.recordTypeId + '', 10) : null;
        st.instrumentTypeId = st.instrumentTypeId ? parseInt(st.instrumentTypeId + '', 10) : null;
        st.countyId = st.countyId ? parseInt(st.countyId + '', 10) : null;
        st.acres = st.acres ? parseFloat(st.acres + '') : 0;
        st.partialAcreage = st.partialAcreage ? parseFloat(st.partialAcreage + '') : 0;

        st.isPartial = st.isPartial ? st.isPartial : false;

        st.name = st.name ? st.name : '';
        st.grantee = st.grantee ? st.grantee : '';
        st.grantor = st.grantor ? st.grantor : '';
        st.book = st.book ? st.book : '';
        st.page = st.page ? st.page : '';
        st.boundByNorth = st.boundByNorth ? st.boundByNorth : '';
        st.boundByEast = st.boundByEast ? st.boundByEast : '';
        st.boundBySouth = st.boundBySouth ? st.boundBySouth : '';
        st.boundByWest = st.boundByWest ? st.boundByWest : '';
        st.legalDescription = st.legalDescription ? st.legalDescription : '';

        st.recordDate = st.recordDate ? (moment(st.recordDate).format('YYYY-MM-DD') as unknown as Date) : null;

        st.tags = [...surfaceTags];
        st.abstracts = [...surfaceAbstracts];

        return st;
    };

    const formatOwnerships = (so: Ownership[]) => {
        if (so?.length > 0) {
            so = so.map((ownership: Ownership) => {
                ownership.id = ownership.id ? parseInt(ownership.id + '', 10) : 0;
                ownership.parentOwnershipId = ownership.parentOwnershipId ? parseInt(ownership.parentOwnershipId + '', 10) : 0;
                ownership.additionalOwnershipTypeId = ownership.additionalOwnershipTypeId
                    ? parseInt(ownership.additionalOwnershipTypeId + '', 10)
                    : 0;
                ownership.ownershipContactCategoryId = ownership.ownershipContactCategoryId
                    ? parseInt(ownership.ownershipContactCategoryId + '', 10)
                    : null;
                ownership.leaseBuyer = ownership.leaseBuyer
                    ? parseInt(ownership.leaseBuyer + '', 10)
                    : null;
                    
                ownership.decimalInterest = ownership.decimalInterest ? parseFloat(ownership.decimalInterest + '') : null;
                ownership.executiveRights = ownership.executiveRights ? parseFloat(ownership.executiveRights + '') : null;

                if (ownership.additionalOwnerships?.length > 0) {
                    ownership.additionalOwnerships = formatOwnerships(ownership.additionalOwnerships);
                }
                return ownership;
            });
        }
        return so;
    };

    const formatMineralTracts = (mt: TractDetail[]) => {
        if (mt?.length > 0) {
            mt = mt.map((tract: TractDetail) => {
                tract.id = tract.id ? parseInt(tract.id + '', 10) : 0;
                tract.tractNumber = tract.tractNumber ? parseInt(tract.tractNumber + '', 10) : 0;
                tract.grossAcres = tract.grossAcres ? parseFloat(tract.grossAcres + '') : 0;
                tract.tractName = tract.tractName ? tract.tractName : '';
                tract.horizons = tract.horizons?.map((horizon: Horizon) => {
                    horizon.id = horizon.id ? parseInt(horizon.id + '', 10) : 0;
                    horizon.partialAcreage = horizon.partialAcreage ? parseFloat(horizon.partialAcreage + '') : 0;

                    horizon.grantee = horizon.grantee ? horizon.grantee : '';
                    horizon.grantor = horizon.grantor ? horizon.grantor : '';
                    horizon.legalDescription = horizon.legalDescription ? horizon.legalDescription : '';

                    horizon.isPartial = horizon.isPartial ? horizon.isPartial : null;

                    if (horizon.mineralOwnerships?.length > 0) {
                        horizon.mineralOwnerships = formatOwnerships(horizon.mineralOwnerships);
                    }
                    return horizon;
                });

                return tract;
            });
        }
        return mt;
    };

    return (
        <div className="tract-detail">
            <br />

            <div className="tract-nav">
                {appContext.sectionNumber && (
                    <h2 style={{ marginRight: '.7rem', fontWeight: 'lighter', marginLeft: '2rem' }}>
                        {`Global ID: ${appContext.clientNumber}.${appContext.projectNumber}.${appContext.sectionNumber}.${appContext.surfaceTractNumber}`}
                    </h2>
                )}

                {appContext.mapNumber && (
                    <h2 style={{ marginRight: '.7rem', fontWeight: 'lighter', marginLeft: '2rem' }}>
                        {`Global ID: ${appContext.clientNumber}.${appContext.projectNumber}.${appContext.mapNumber}.${appContext.surfaceTractNumber}`}
                    </h2>
                )}
            </div>

            <div className="tract-info-form">
                {surfaceTract && projectData && (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {(formState.isDirty || fieldsUpdated) && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(191, 195, 203, .7)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        borderTop: '10px solid rgba(66, 184, 241, 1)',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            display: 'flex',
                                        }}
                                    >
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" disabled={formState.isSubmitting} />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button
                                            text="Cancel Changes"
                                            size="small"
                                            disabled={!formState.isDirty || formState.isSubmitting}
                                            onClick={cancelFormChanges}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="clearfix tract-data">
                            <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0', marginBottom: '.2rem' }}>
                                <legend className="legend">Surface Tract Information</legend>
                            </fieldset>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <SurfaceTractCardRHF
                                    surfaceTract={surfaceTract}
                                    landSystem={projectData?.landSystemId}
                                    counties={projectData?.counties}
                                    projectTags={projectData?.tags}
                                    surfaceTags={surfaceTags}
                                    surfaceAbstracts={surfaceAbstracts}
                                    updateSurfaceTags={(tags: Tag[]) => updateSurfaceTags(tags)}
                                    updateSurfaceAbstracts={(abstracts: Abstract[]) => updateSurfaceAbstracts(abstracts)}
                                    register={register}
                                    errors={errors}
                                    getValues={getValues}
                                    setValue={setValue}
                                    formState={formState}
                                    onChanges={() => loadSurfaceTract()}
                                    updateGrossAcres={() => updateGrossAcres()}
                                    updateSurfaceTract={(cloneTract: SurfaceTract) => onSubmit(cloneTract)}
                                />
                            </div>
                        </div>

                        <div className="ownership-breakdown">
                            <GlobalOwnershipList
                                tractId={surfaceTract?.id}
                                namePrefix={'ownerships'}
                                ownershipContactCategories={ownershipContactCategories?.categories}
                                projectType={'Surface'}
                                ownershipType={'Surface'}
                                ownerships={surfaceTract?.ownerships}
                                owners={surfaceTract?.owners}
                                grossAcres={grossAcres}
                                leaseBuyerList={leaseBuyerList}
                                loadSurfaceTract={() => loadSurfaceTract()}
                                register={register}
                                errors={errors}
                                getValues={getValues}
                                formState={formState}
                                reset={() => reset(surfaceTract)}
                            />
                        </div>
                        <div>
                            <MineralTractList
                                landSystem={projectData?.landSystemId}
                                ownershipContactCategories={ownershipContactCategories?.categories}
                                mineralTracts={surfaceTract.mineralTracts}
                                counties={projectData?.counties}
                                leaseBuyerList={leaseBuyerList}
                                loadSurfaceTract={() => loadSurfaceTract()}
                                openContactNotes={function (ownership: Ownership): void {
                                    throw new Error('Function not implemented.');
                                }}
                                register={register}
                                errors={errors}
                                getValues={getValues}
                                formState={formState}
                                reset={() => reset(surfaceTract)}
                            />
                        </div>

                        <div style={{ textAlign: 'right', margin: '1rem 1rem .7rem 0' }}>
                            <Button text="Save" type="submit" size="medium" intent="primary" disabled={formState.isSubmitting} />{' '}
                            <Button text="Cancel" size="medium" disabled={!formState.isDirty || formState.isSubmitting} onClick={cancelFormChanges} />
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default SurfaceTractPage;



// WEBPACK FOOTER //
// ./src/components/project/sections/SurfaceTractPage.tsx